import { useState } from 'react';
import { Box, Card, Flex, Heading, IconButton, Menu, Portal, Text } from '@chakra-ui/react';
import { LuEllipsisVertical } from 'react-icons/lu';
import { useAuthContext } from '@contexts/auth';
import CreatedBy from '@components/CreatedBy';

import type { OptionType } from '../types';
import ChangeStatusDialog from './ChangeStatusDialog';
import EditDialog from './EditDialog';
import { BEHAVIOURS_LABEL } from '../behaviours';

type Props = {
  option: OptionType,
  refetch: () => void,
};

const OptionCard = ({ option, refetch }: Props) => {
  const { checkIfHasRole } = useAuthContext();

  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showChangeStatusDialog, setShowChangeStatusDialog] = useState(false);

  const isEnabled = option.is_enabled;

  const canEditOption = checkIfHasRole('CAN_EDIT_TABULATION_OPTIONS');
  const canDisableOption = checkIfHasRole('CAN_DISABLE_TABULATION_OPTIONS');

  const showMenu = canEditOption || canDisableOption;

  return (
    <Card.Root
      size="sm"
      variant="elevated"
      bg="grayscale.fg"
      mt={4}
      px={4}
      py={3}
    >
      <Flex justifyContent="space-between" alignItems="center" gap={2}>
        <Box>
          <Heading
            size="md"
            textDecoration={isEnabled ? 'none' : 'line-through'}
            color={isEnabled ? 'fg.default' : 'fg.error'}
          >
            {option.name}
          </Heading>
          <Text fontSize="sm" color="argos.solid" fontWeight={500}>
            {BEHAVIOURS_LABEL[option.behaviour]}
          </Text>
          <CreatedBy user={option.user} createdDate={option.created_date} />
        </Box>
        {showMenu && (
          <Menu.Root>
            <Menu.Trigger asChild>
              <IconButton variant="ghost" size="sm">
                <LuEllipsisVertical />
              </IconButton>
            </Menu.Trigger>
            <Portal>
              <Menu.Positioner>
                <Menu.Content>
                  {canEditOption && (
                    <Menu.Item value="edit" onClick={() => setShowEditDialog(true)}>
                      Editar
                    </Menu.Item>
                  )}
                  {canDisableOption && (
                    <Menu.Item
                      value="changeStatus"
                      color={isEnabled ? "fg.error" : "fg.success"}
                      _hover={{
                        bg: isEnabled ? "bg.error" : "bg.success",
                        color: isEnabled ? "fg.error" : "fg.success"
                      }}
                      onClick={() => setShowChangeStatusDialog(true)}
                    >
                      {isEnabled ? 'Desabilitar' : 'Habilitar'}
                    </Menu.Item>
                  )}
                </Menu.Content>
              </Menu.Positioner>
            </Portal>
          </Menu.Root>     
        )}
      </Flex>

      {showEditDialog && (
        <EditDialog
          handleClose={() => setShowEditDialog(false)}
          afterSubmit={refetch}
          option={option}
        />
      )}
      {showChangeStatusDialog && (
        <ChangeStatusDialog
          option={option}
          handleClose={() => setShowChangeStatusDialog(false)}
          afterSubmit={refetch}
        />
      )}
    </Card.Root>
  );
};

export default OptionCard;
